import { useState, useCallback, useEffect } from 'react';
import { useSelectableCollectionKeyboard } from './useSelectableCollectionKeyboard.js';
import { useTypeKeySearch } from './useTypeKeySearch.js';
import { useCollection } from '../../hooks/useCollection.js';
import { useControllableState } from '../../hooks/useControllableState.js';
import '../../utils/warn.js';

const useListbox = props => {
  const {
    scrollContainerRef,
    multiselect,
    defaultSelectedValues,
    selectedValues,
    onSelectionChange
  } = props;
  const {
    registerItem,
    getItemIndexById,
    getItemCount,
    getItemByIndex,
    getItemById,
    getAllItems,
    getItemsByPredicate
  } = useCollection();
  const [focusVisible, setFocusVisible] = useState(false);
  const [activeOption, setActiveOption] = useState(undefined);
  const [selectedOptionValues, setSelectedOptionValues] = useControllableState({
    state: selectedValues,
    defaultState: defaultSelectedValues,
    initialState: []
  });
  const selectOption = useCallback(option => {
    if (option.disabled) return;
    let newSelectedValues = [option.value];
    if (multiselect) {
      const selectedIndex = selectedOptionValues.findIndex(
      /**
       * Compare values since automatically generated ids can change when
       * mounted/unmounted (e.g., filtering `Option`s).
       */
      value => value === option.value);
      if (selectedIndex > -1) {
        newSelectedValues = [...selectedOptionValues.slice(0, selectedIndex), ...selectedOptionValues.slice(selectedIndex + 1)];
      } else {
        newSelectedValues = [...selectedOptionValues, option.value];
      }
    }
    setSelectedOptionValues(newSelectedValues);
    onSelectionChange?.(newSelectedValues);
  }, [multiselect, onSelectionChange, selectedOptionValues, setSelectedOptionValues]);

  // Place virtual focus on the first selected option or first option if nothing selected
  const initializeVirtualFocus = useCallback(() => {
    const optionToFocus = selectedOptionValues.length ? getItemsByPredicate(option => selectedOptionValues.includes(option.value))[0] : getItemByIndex(0);
    setFocusVisible(true);
    setActiveOption(optionToFocus);
  }, [getItemByIndex, getItemsByPredicate, selectedOptionValues]);

  // Scroll currently active option into view
  useEffect(() => {
    if (activeOption?.ref?.current && scrollContainerRef?.current) {
      scrollOptionIntoView(scrollContainerRef.current, activeOption.ref.current, getItemByIndex(0).ref.current);
    }
  }, [activeOption, getItemByIndex, scrollContainerRef]);
  const scrollToFirstSelection = useCallback(() => {
    const optionToScrollTo = getItemsByPredicate(option => selectedOptionValues.includes(option.value))[0];
    if (optionToScrollTo && optionToScrollTo.ref.current && scrollContainerRef?.current) {
      scrollOptionIntoView(scrollContainerRef.current, optionToScrollTo.ref.current, getItemByIndex(0).ref.current);
    }
  }, [getItemByIndex, getItemsByPredicate, scrollContainerRef, selectedOptionValues]);
  const {
    onKeyDown
  } = useSelectableCollectionKeyboard({
    activeIndex: () => activeOption ? getItemIndexById(activeOption?.id) : -1,
    collectionLength: () => getItemCount(),
    onIndexChange: nextItemIndex => {
      setActiveOption(getItemByIndex(nextItemIndex));
      setFocusVisible(true);
    },
    onSelect: () => {
      if (activeOption && !activeOption.disabled) {
        selectOption(activeOption);
      }
    }
  });
  const {
    onKeyDownCapture
  } = useTypeKeySearch({
    onTypeKeySearch: key => {
      const allSameCharacter = key.length > 0 && key.split("").every(char => char === key[0]);
      const matchedOptions = getItemsByPredicate(item => {
        let search = key;

        // If the key search is all the same character, reduce to just the first character
        // so the regex still returns all items that start with that character, i.e. cccc = c.
        if (allSameCharacter) {
          search = key[0];
        }

        // Escape all special characters in search string
        search = search.replace(/[^a-zA-Z0-9]/g, "\\$&");
        const regex = new RegExp(`^${search}`, "gi");
        const stringToTest = item.text || item.value;
        return regex.test(stringToTest);
      });
      if (matchedOptions.length) {
        // If the key search is all the same character, cycle active item through all options
        // that begin with that character.
        let activeIndex = 0;
        if (key.length > 1 && allSameCharacter) {
          activeIndex = (key.length - 1) % matchedOptions.length;
        }
        setActiveOption(matchedOptions[activeIndex]);
      }
    }
  });
  return {
    focusVisible,
    setFocusVisible,
    initializeVirtualFocus,
    activeOption,
    setActiveOption,
    selectOption,
    selectedOptionValues,
    onKeyDown,
    onKeyDownCapture,
    scrollToFirstSelection,
    collectionState: {
      registerItem,
      getItemIndexById,
      getItemCount,
      getItemByIndex,
      getItemById,
      getAllItems,
      getItemsByPredicate
    }
  };
};

/**
 * Scrolls `view` container so that `option` is visible.
 *
 * Mimics the native `element.scrollIntoView({ block: "nearest" })` without causing
 * ancestor containers to scroll.
 * @see https://github.com/w3c/csswg-drafts/issues/9452
 *
 * Consider generalizing this function if there's a shared need.
 */
function scrollOptionIntoView(view, option, firstOption) {
  const {
    offsetHeight,
    offsetTop
  } = option;
  const {
    offsetHeight: viewOffsetHeight,
    scrollTop: viewScrollTop
  } = view;
  const bufferBottom = parseInt(getComputedStyle(view).paddingBlockEnd, 10);
  /**
   * Use the first option in the collection to determine the initial offset.
   * This will account for "filterable" select lists that include a sticky
   * input as the first descendant of the popup.
   */
  const bufferTop = firstOption ? firstOption.offsetTop : parseInt(getComputedStyle(view).paddingBlockStart, 10);
  const isAbove = offsetTop - bufferTop < viewScrollTop;
  const isBelow = offsetTop + offsetHeight > viewScrollTop + viewOffsetHeight;
  if (isAbove) {
    view.scrollTo(0, offsetTop - bufferTop);
  } else if (isBelow) {
    view.scrollTo(0, offsetTop - viewOffsetHeight + offsetHeight + bufferBottom);
  }
}

export { useListbox };
